import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { User, Mail, Phone, MapPin, Edit, Save, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Details = () => {
    const { user } = useAuth();
    const [parentData, setParentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        mobile: '',
        email: '',
        address: ''
    });

    useEffect(() => {
        fetchParentData();
    }, [user]);

    const fetchParentData = async () => {
        try {
            const effectiveId = user?.linked_parent_id || user?.id;
            const response = await axios.get(`${API_URL}/api/parents/${effectiveId}`, { withCredentials: true });
            setParentData(response.data);
            setFormData({
                mobile: response.data.mobile || '',
                email: response.data.email || '',
                address: response.data.address || ''
            });
        } catch (error) {
            toast.error('Failed to load your details');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const effectiveId = user?.linked_parent_id || user?.id;
            await axios.put(`${API_URL}/api/parents/${effectiveId}`, formData, { withCredentials: true });
            toast.success('Details updated successfully');
            setEditing(false);
            fetchParentData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update details');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="parent-details-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">My Details</h1>
                        <p className="text-[#4B5563]">View and update your personal information</p>
                    </div>
                    {!editing ? (
                        <Button onClick={() => setEditing(true)} className="bg-[#064E3B] hover:bg-[#047857]">
                            <Edit className="h-4 w-4 mr-2" /> Edit Details
                        </Button>
                    ) : (
                        <div className="flex space-x-2">
                            <Button variant="outline" onClick={() => setEditing(false)}>
                                <X className="h-4 w-4 mr-2" /> Cancel
                            </Button>
                            <Button onClick={handleSave} disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                                Save
                            </Button>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Profile Card */}
                    <Card className="card-surface lg:col-span-1">
                        <CardContent className="p-6 flex flex-col items-center text-center">
                            <div className="h-24 w-24 rounded-full bg-[#064E3B] flex items-center justify-center text-white text-3xl font-bold mb-4">
                                {parentData?.first_name?.[0]}{parentData?.last_name?.[0]}
                            </div>
                            <h2 className="text-xl font-semibold text-[#1F2937]">{parentData?.first_name} {parentData?.last_name}</h2>
                            <Badge className="mt-2 bg-[#D97706]">{parentData?.account_type === 'family' ? 'Family Account' : 'Adult Student'}</Badge>
                            <div className="mt-4 p-3 bg-[#FAF9F6] rounded-lg w-full">
                                <p className="text-xs text-[#4B5563]">Registration Code</p>
                                <p className="text-lg font-mono font-semibold text-[#064E3B]">{parentData?.reg_code}</p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Details Card — Guardian 1 */}
                    <Card className="card-surface lg:col-span-2">
                        <CardHeader>
                            <CardTitle className="font-['Outfit']">Guardian 1 (Primary)</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Read-only fields */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-[#4B5563]">First Name</Label>
                                    <div className="flex items-center p-3 bg-[#FAF9F6] rounded-lg">
                                        <User className="h-4 w-4 mr-2 text-[#4B5563]" />
                                        <span className="font-medium">{parentData?.first_name}</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-[#4B5563]">Last Name</Label>
                                    <div className="flex items-center p-3 bg-[#FAF9F6] rounded-lg">
                                        <User className="h-4 w-4 mr-2 text-[#4B5563]" />
                                        <span className="font-medium">{parentData?.last_name}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Editable fields */}
                            <div className="space-y-2">
                                <Label className="text-[#4B5563]">Email Address</Label>
                                {editing ? (
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                        <Input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            className="pl-9"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center p-3 bg-[#FAF9F6] rounded-lg">
                                        <Mail className="h-4 w-4 mr-2 text-[#4B5563]" />
                                        <span className="font-medium">{parentData?.email}</span>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label className="text-[#4B5563]">Mobile Number</Label>
                                {editing ? (
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                        <Input
                                            value={formData.mobile}
                                            onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                                            className="pl-9"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center p-3 bg-[#FAF9F6] rounded-lg">
                                        <Phone className="h-4 w-4 mr-2 text-[#4B5563]" />
                                        <span className="font-medium">{parentData?.mobile}</span>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-2">
                                <Label className="text-[#4B5563]">Home Address</Label>
                                {editing ? (
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-3 h-4 w-4 text-[#4B5563]" />
                                        <Input
                                            value={formData.address}
                                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                                            className="pl-9"
                                        />
                                    </div>
                                ) : (
                                    <div className="flex items-center p-3 bg-[#FAF9F6] rounded-lg">
                                        <MapPin className="h-4 w-4 mr-2 text-[#4B5563]" />
                                        <span className="font-medium">{parentData?.address}</span>
                                    </div>
                                )}
                            </div>

                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-700">
                                    <strong>Note:</strong> To change your name or registration code, please contact the school administration.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Guardian 2 Card */}
                    <Card className="card-surface lg:col-span-3" data-testid="parent-guardian2-card">
                        <CardHeader>
                            <CardTitle className="font-['Outfit']">Guardian 2 (Secondary)</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {(parentData?.guardian2_first_name || parentData?.guardian2_last_name || parentData?.guardian2_email || parentData?.guardian2_mobile) ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-[#4B5563]">Full Name</Label>
                                        <div className="flex items-center p-3 bg-[#FAF9F6] rounded-lg">
                                            <User className="h-4 w-4 mr-2 text-[#4B5563]" />
                                            <span className="font-medium">{`${parentData?.guardian2_first_name || ''} ${parentData?.guardian2_last_name || ''}`.trim() || '—'}</span>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-[#4B5563]">Email</Label>
                                        <div className="flex items-center p-3 bg-[#FAF9F6] rounded-lg">
                                            <Mail className="h-4 w-4 mr-2 text-[#4B5563]" />
                                            <span className="font-medium">{parentData?.guardian2_email || '—'}</span>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-[#4B5563]">Mobile</Label>
                                        <div className="flex items-center p-3 bg-[#FAF9F6] rounded-lg">
                                            <Phone className="h-4 w-4 mr-2 text-[#4B5563]" />
                                            <span className="font-medium">{parentData?.guardian2_mobile || '—'}</span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-6 text-[#4B5563]">
                                    <User className="h-10 w-10 mb-2 opacity-20" />
                                    <p className="text-sm">No second guardian registered for this account</p>
                                    <p className="text-xs mt-1">Contact school administration to add Guardian 2 details.</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Details;
